import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
	type ChangeEvent,
	type DragEvent,
	type KeyboardEvent,
	useCallback,
	useRef,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakpoint } from '@/utils/useBreakpoint';

import type { AddFilesResult } from '../../_hooks/useAttachmentUpload';
import {
	ATTACHMENT_ACCEPT_ATTR,
	ATTACHMENT_MAX_FILES,
	type DcrAttachment
} from '../../_types/attachment';
import { AttachmentThumbnail } from '../AttachmentThumbnail/AttachmentThumbnail';
import { AttachmentViewer } from '../AttachmentViewer/AttachmentViewer';

import { attachmentDropzoneStyles as sx } from './AttachmentDropzone.styles';

type Props = {
	attachments: DcrAttachment[];
	addFiles: (files: File[]) => AddFilesResult;
	removeAttachment: (id: string) => void;
};

export const AttachmentDropzone = ({
	attachments,
	addFiles,
	removeAttachment
}: Props) => {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [viewerIndex, setViewerIndex] = useState<number | null>(null);
	const isXs = useBreakpoint('sm', 'down');

	const slotsLeft = ATTACHMENT_MAX_FILES - attachments.length;
	const canAddMore = slotsLeft > 0;

	const handleFiles = useCallback(
		(files: File[]) => {
			if (files.length === 0) return;
			const result = addFiles(files);
			const newErrors = Array.from(new Set(result.rejected.map(r => r.error)));
			setErrors(newErrors);
		},
		[addFiles]
	);

	const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		handleFiles(Array.from(e.target.files));
		e.target.value = '';
	};

	const handleBrowseClick = () => {
		inputRef.current?.click();
	};

	const handleZoneKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleBrowseClick();
		}
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (canAddMore) setIsDraggingOver(true);
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOver(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOver(false);
		if (!canAddMore) return;
		const files = Array.from(e.dataTransfer.files);
		handleFiles(files);
	};

	const handleThumbnailClick = (attachment: DcrAttachment) => {
		const idx = attachments.findIndex(a => a.id === attachment.id);
		if (idx >= 0) setViewerIndex(idx);
	};

	const handleViewerClose = () => setViewerIndex(null);

	return (
		<Box sx={{ width: '100%' }}>
			<Box
				sx={sx.root(isDraggingOver, canAddMore)}
				onDragOver={handleDragOver}
				onDragEnter={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={canAddMore ? handleBrowseClick : undefined}
				role={canAddMore ? 'button' : undefined}
				tabIndex={canAddMore ? 0 : undefined}
				onKeyDown={canAddMore ? handleZoneKeyDown : undefined}
			>
				{attachments.length > 0 && (
					<Box sx={sx.thumbnailRow}>
						{attachments.map(att => (
							<AttachmentThumbnail
								key={att.id}
								attachment={att}
								onRemove={removeAttachment}
								onClick={handleThumbnailClick}
							/>
						))}
					</Box>
				)}

				{canAddMore && (
					<Box sx={sx.browseSection(attachments.length > 0)}>
						<input
							ref={inputRef}
							type='file'
							accept={ATTACHMENT_ACCEPT_ATTR}
							multiple
							style={sx.hiddenInput}
							onChange={handleFileInputChange}
						/>
						<Button
							variant='outlined'
							size='small'
							onClick={e => {
								e.stopPropagation();
								handleBrowseClick();
							}}
						>
							{t('Click to browse')}
						</Button>
						<Typography sx={sx.browseSubtext}>
							{t('or drag a file to upload')}
						</Typography>
						{!isXs && (
							<Typography sx={sx.helperText}>
								{t('images only • max {{max}} files • up to 10MB per file', {
									max: ATTACHMENT_MAX_FILES
								})}
							</Typography>
						)}
					</Box>
				)}

				{!canAddMore && (
					<Typography sx={sx.maxReachedText}>
						{t('Maximum number of attachments added')}
					</Typography>
				)}
			</Box>

			{errors.length > 0 && (
				<Box sx={sx.errorList}>
					{errors.map(err => (
						<Alert
							key={err}
							severity='error'
							onClose={() => setErrors(errors.filter(e => e !== err))}
						>
							{err}
						</Alert>
					))}
				</Box>
			)}

			{viewerIndex !== null && (
				<AttachmentViewer
					attachments={attachments}
					initialIndex={viewerIndex}
					onClose={handleViewerClose}
				/>
			)}
		</Box>
	);
};
